"use client";

import { firstPurchasePromoStore } from "@/store/firstPurchasePromo";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import Button from "../../../component/Button";
import styles from "./FirstPurchaseBanner.module.css";

export interface FirstPurchaseBannerProps {
  /** 点击"Claim it Now"按钮回调 */
  onClaim?: () => void;
  /** 倒计时结束时间（毫秒时间戳），默认为 35 分钟后 */
  endTime?: number;
}

interface CountdownTime {
  minutes: number;
  seconds: number;
}

/**
 * 首单优惠横幅组件
 *
 * @example
 * ```tsx
 * <FirstPurchaseBanner
 *   onClaim={() => {
 *     console.log('User clicked claim button');
 *   }}
 * />
 * ```
 */
export const FirstPurchaseBanner = observer(function FirstPurchaseBanner({
  onClaim,
  endTime,
}: FirstPurchaseBannerProps) {
  const [countdown, setCountdown] = useState<CountdownTime>({
    minutes: 0,
    seconds: 0,
  });

  // 倒计时逻辑
  useEffect(() => {
    // 优先级：props.endTime > store.promoEndTime > 当前时间 + 默认时长
    const targetTime =
      endTime || firstPurchasePromoStore.promoEndTime || Date.now();

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, targetTime - now);

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      setCountdown({ minutes, seconds });

      // 倒计时结束，清理 Store 中的时间，触发 Banner 隐藏
      if (remaining <= 0 && firstPurchasePromoStore.isTimerActive) {
        firstPurchasePromoStore.clearPromoTimer();
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [endTime, firstPurchasePromoStore.promoEndTime]);

  const handleClaim = () => {
    onClaim?.();
  };

  return (
    <div
      className={classNames(
        "h-full relative z-[1] rounded-[24px] overflow-hidden mb-[42px] mx-auto",
        "flex justify-center items-center flex-col px-5 pt-8 pb-0 gap-2",
        styles.banner,
      )}
    >
      {/* 左侧：促销信息 */}
      <div
        className={classNames(
          "flex-shrink-0 flex flex-col items-center font-inter",
          styles.promoInfo,
        )}
      >
        {/* 标题和标签 */}
        <div className="flex items-center gap-2">
          <h2 className="font-bold text-[20px] leading-normal bg-gradient-to-r from-[#baa8ff] to-[#baa8ff] bg-clip-text text-transparent">
            FIRST PURCHASE
          </h2>
          <div className="h-6 px-2 py-1 bg-[#FF6752] rounded-[6px] font-semibold text-xs leading-normal text-white">
            Limited Offer
          </div>
        </div>

        {/* 折扣信息 */}
        <h1 className="font-extrabold text-[64px] leading-[77px] text-white">
          33% OFF
        </h1>
        <p className="font-semibold text-2xl leading-none text-[#baa8ff] opacity-60">
          UP TO $631.2 OFF
        </p>

        <Button
          type="primary"
          onClick={handleClaim}
          className="mt-[9px] rounded-xl w-60 h-[52px] text-[20px] font-semibold "
        >
          Claim it Now
        </Button>
      </div>

      {/* 右侧：倒计时 */}
      <div className={classNames(styles.countdown)}>
        {/* 倒计时标题 */}
        <p className={styles.countdownTitle}>One-time Offer Expires in...</p>

        {/* 倒计时显示 */}
        <div className={styles.countdownDisplay}>
          <div className={styles.countdownBox}>
            <span className={styles.countdownNumber}>
              {countdown.minutes.toString().padStart(2, "0")}
            </span>
            <span className={styles.countdownLabel}>minutes</span>
          </div>

          <div className={styles.countdownBox}>
            <span className={styles.countdownNumber}>
              {countdown.seconds.toString().padStart(2, "0")}
            </span>
            <span className={styles.countdownLabel}>seconds</span>
          </div>
        </div>
      </div>
    </div>
  );
});
